import type { EmailAttachment } from './attachments';

/** Mirrors Prisma `EmailAttachmentTextExtractionStatus` */
export type AttachmentTextExtractionStatus =
  | 'PENDING'
  | 'OK'
  | 'FAILED'
  | 'UNSUPPORTED'
  | 'SKIPPED';

export type PersistedEmailAttachmentRow = {
  id: number;
  filename: string;
  contentType: string;
  size: number;
  textExtractionStatus: AttachmentTextExtractionStatus;
  textExtractionError?: string | null;
  textExtractedAt?: string | null;
};

/**
 * Match JSON metadata attachment to persisted `emailAttachments` row (same order as backend non-inline list).
 */
export function matchPersistedEmailAttachment(
  index: number,
  meta: EmailAttachment,
  rows: PersistedEmailAttachmentRow[] | undefined | null,
): PersistedEmailAttachmentRow | undefined {
  if (!rows?.length) return undefined;
  if (index < rows.length) {
    const r = rows[index];
    if (r.size === meta.size && (!meta.filename || r.filename === meta.filename)) {
      return r;
    }
  }
  return rows.find((r) => r.filename === meta.filename && r.size === meta.size);
}

export function attachmentTextExtractionHint(
  status: AttachmentTextExtractionStatus,
  error?: string | null,
): string {
  const err = error?.trim();
  switch (status) {
    case 'PENDING':
      return 'Search text: not extracted yet (queued)';
    case 'OK':
      return 'Search text: extracted';
    case 'FAILED':
      return err ? `Text extraction failed: ${err}` : 'Text extraction failed';
    case 'UNSUPPORTED':
      return 'Search text: not available for this file type';
    case 'SKIPPED':
      return err ? `Search text skipped: ${err}` : 'Search text skipped (e.g. file too large)';
    default:
      return 'Search text status unknown';
  }
}

export function attachmentTextExtractionDotClass(status: AttachmentTextExtractionStatus): string {
  switch (status) {
    case 'PENDING':
      return 'bg-amber-400 dark:bg-amber-500';
    case 'OK':
      return 'bg-emerald-500 dark:bg-emerald-400';
    case 'FAILED':
      return 'bg-red-500 dark:bg-red-400';
    case 'UNSUPPORTED':
      return 'bg-slate-400 dark:bg-slate-500';
    case 'SKIPPED':
      return 'bg-orange-400 dark:bg-orange-500';
    default:
      return 'bg-gray-400';
  }
}
